import React, { useEffect, useRef } from 'react';
import { X, CheckCircle } from 'lucide-react';
import Stepper from './Stepper';
import { TONE } from './reimbursementStatus';
import { fieldDomId, firstErrorField } from './formHelpers';

// The Reimbursement modal's frame, shared by the section and its forms. Only one is shown at a
// time, so they share the title id the dialog is labelled by (PhonepeDashboard).
export const DIALOG_TITLE_ID = 'reimbursement-dialog-title';

export const PRIMARY_BUTTON = 'flex-1 inline-flex items-center justify-center gap-2 py-3 px-4 text-sm font-semibold text-white bg-portal-purple hover:bg-portal-purple-dark rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
export const SECONDARY_BUTTON = 'flex-1 inline-flex items-center justify-center gap-2 py-3 px-4 text-sm font-semibold text-portal-ink bg-white border border-portal-border hover:bg-portal-bg rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
export const SECTION_HEADING = 'text-xs font-bold uppercase tracking-[1px] text-portal-sub';

export const DialogHeader = ({ title, subtitle, onClose, children }) => (
    <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3">
        <div className="min-w-0">
            <h2 id={DIALOG_TITLE_ID} className="text-lg font-bold text-portal-ink leading-snug">{title}</h2>
            {subtitle && <p className="text-xs text-portal-sub mt-0.5">{subtitle}</p>}
            {children}
        </div>
        <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-9 h-9 -mr-1.5 rounded-full flex items-center justify-center text-portal-sub hover:text-portal-ink hover:bg-portal-bg transition-colors flex-shrink-0"
        >
            <X className="w-5 h-5" aria-hidden="true" />
        </button>
    </div>
);

// Header and stepper on top, a scrolling body, and a footer that stays visible below it (clear of
// the iPhone home bar). `error` is a form-level message (e.g. from the server), shown at the top.
// A new `scrollKey` (e.g. the wizard step) starts the body at the top again.
const FormShell = ({ title, subtitle, steps, stepsLabel = 'Form progress', onClose, error, footer, children, testId, scrollKey }) => {
    const bodyRef = useRef(null);

    useEffect(() => {
        if (error) bodyRef.current?.scrollTo?.({ top: 0, behavior: 'smooth' });
    }, [error]);

    useEffect(() => {
        if (bodyRef.current) bodyRef.current.scrollTop = 0;
    }, [scrollKey]);

    return (
        <>
            <div className="flex-shrink-0 border-b border-portal-border">
                <DialogHeader title={title} subtitle={subtitle} onClose={onClose} />
                {steps && <Stepper steps={steps} label={stepsLabel} className="px-3 pb-3" />}
            </div>
            <div ref={bodyRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 py-4" data-testid={testId}>
                {error && (
                    <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3 mb-4">{error}</p>
                )}
                {children}
            </div>
            {footer && (
                <div className="flex-shrink-0 border-t border-portal-border bg-white px-5 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
                    {footer}
                </div>
            )}
        </>
    );
};

// The screen after a form is sent. `status` is the status the Reimbursement moved to.
export const FormDone = ({ testId, title, body, status, onDone }) => {
    const doneRef = useRef(null);
    useEffect(() => { doneRef.current?.focus(); }, []);

    return (
        <div className="p-6 sm:p-8 pb-[max(1.5rem,env(safe-area-inset-bottom))] flex flex-col items-center text-center overflow-y-auto" data-testid={testId}>
            <div className="w-16 h-16 bg-portal-green-light rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-portal-green" aria-hidden="true" />
            </div>
            <h3 id={DIALOG_TITLE_ID} className="text-portal-ink font-bold text-lg mb-1">{title}</h3>
            <p className={`text-portal-sub text-sm ${status ? 'mb-3' : 'mb-6'}`}>{body}</p>
            {status && (
                <p className={`text-xs font-semibold border rounded-full px-3 py-1 mb-6 ${TONE.review}`}>Status: {status}</p>
            )}
            <button ref={doneRef} type="button" onClick={onDone} className={`${PRIMARY_BUTTON} w-full`}>
                Done
            </button>
        </div>
    );
};

// Scrolls to the first field with an error and focuses it, once React has shown the errors.
export const focusFirstError = (formId, errors, order) => {
    const name = firstErrorField(errors, order);
    if (!name) return;
    window.requestAnimationFrame(() => {
        const element = document.getElementById(fieldDomId(formId, name));
        if (!element) return;
        element.scrollIntoView({ block: 'center', behavior: 'smooth' });
        element.focus({ preventScroll: true });
    });
};

export default FormShell;
